import { Helmet } from 'react-helmet-async'
import { ContactForm } from '@/components/ContactForm'
import { BadgeCheck, Clock3, MessageSquare, ShieldCheck } from 'lucide-react'
import { BRAND_NAME, getPageTitle } from '@/lib/branding'

export function ContactPage() {
  return (
    <>
      <Helmet>
        <title>{getPageTitle('Contact')}</title>
        <meta
          name="description"
          content={`Send a message to the ${BRAND_NAME} team. Ask product questions, suggest gear to review, or get help with your order.`}
        />
      </Helmet>

      <div className="flex flex-1 flex-col bg-zinc-50">
        <section className="relative overflow-hidden border-b border-zinc-200 py-14 md:py-16">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute inset-x-0 top-0 h-36 bg-gradient-to-b from-zinc-100 to-transparent" />
            <div className="absolute -left-24 top-10 h-56 w-56 rounded-full bg-red-100/50 blur-3xl" />
            <div className="absolute -right-24 bottom-10 h-56 w-56 rounded-full bg-zinc-200/70 blur-3xl" />
          </div>

          <div className="container relative mx-auto px-4 text-center">
            <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-zinc-300/90 bg-white/90 px-4 py-1.5 text-sm font-medium text-zinc-700">
              <MessageSquare className="size-4" />
              Contact
            </span>
            <h1 className="mx-auto mb-4 max-w-4xl text-4xl font-bold text-zinc-900 md:text-5xl">
              Talk to the Team
            </h1>
            <p className="mx-auto max-w-2xl text-lg text-zinc-600">
              Ask a product question, suggest gear for a future review, or get
              help with an order. We read every message.
            </p>
          </div>
        </section>

        <section className="container mx-auto w-full px-4 py-12 md:py-14">
          <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-[1.2fr_0.8fr]">
            <div className="rounded-2xl border border-zinc-200 bg-white p-6 md:p-8">
              <h2 className="mb-2 text-2xl font-semibold text-zinc-900">
                Send Us a Message
              </h2>
              <p className="mb-6 text-zinc-600">
                Use the form below and we&apos;ll route your message to the right
                team.
              </p>
              <ContactForm />
            </div>

            <div className="space-y-4">
              <div className="rounded-2xl border border-zinc-200 bg-white p-6">
                <h3 className="mb-4 text-lg font-semibold text-zinc-900">
                  What to Include
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <BadgeCheck className="mt-0.5 size-5 text-red-600" />
                    <p className="text-sm text-zinc-700">
                      Clear subject line so we can route your request quickly.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <BadgeCheck className="mt-0.5 size-5 text-red-600" />
                    <p className="text-sm text-zinc-700">
                      Relevant product or order details when you need support.
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <BadgeCheck className="mt-0.5 size-5 text-red-600" />
                    <p className="text-sm text-zinc-700">
                      Your best contact email so we can reply directly.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-zinc-200 bg-white p-6">
                <div className="flex items-start gap-3">
                  <Clock3 className="mt-0.5 size-5 text-red-600" />
                  <div>
                    <p className="font-medium text-zinc-900">Typical Response</p>
                    <p className="text-sm text-zinc-600">
                      Most messages receive a reply within 1-2 business days.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-zinc-200 bg-white p-6">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="mt-0.5 size-5 text-red-600" />
                  <p className="text-sm text-zinc-700">
                    Your message is sent securely to our team.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}
